// v2/layout/Header.tsx — Sticky top header with logo and user profile menu

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, LogOut, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@components/shadcn-ui/dropdown-menu';
import { Badge } from '@components/shadcn-ui/badge';
import { useAuth } from 'context/AuthContext';

interface HeaderProps {
  onMenuToggle?: () => void;
}

export function Header({ onMenuToggle }: HeaderProps): React.ReactElement {
  const { userDetails, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatRole = (role?: string) => {
    if (!role) return '';
    return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-b-primary/20 bg-background px-4 md:px-6 shadow-sm">
      {/* Left: hamburger (mobile) + logo */}
      <div className="flex items-center gap-3">
        {onMenuToggle && (
          <button
            onClick={onMenuToggle}
            className="md:hidden flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg hover:bg-accent transition-colors"
            aria-label="Toggle navigation menu"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}
        <Link to="/v2/" className="flex items-center">
          <img src="/assets/logo.png" alt="Logo" className="h-8" />
        </Link>
      </div>

      {/* Right: user profile dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-accent transition-colors min-h-[44px]">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
              {userDetails?.userName?.[0]?.toUpperCase() ?? 'U'}
            </div>
            <span className="text-sm font-medium hidden sm:inline max-w-[120px] truncate">
              {userDetails?.userName ?? 'User'}
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">{userDetails?.userName ?? 'User'}</span>
              {userDetails?.email && (
                <span className="text-xs text-muted-foreground break-all">{userDetails.email}</span>
              )}
              {userDetails?.role && (
                <Badge variant="secondary" className="w-fit text-xs mt-1">
                  {formatRole(userDetails.role)}
                </Badge>
              )}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer">
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
